import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface ResumeStateData {
  canResume: boolean;
  pendingCount: number;
  processedCount: number;
  totalCount: number;
  hasStoppedGeneration: boolean;
}

export function useCollectionResumeState(collectionId: string) {
  return useQuery({
    queryKey: ['collection-resume-state', collectionId],
    queryFn: async (): Promise<ResumeStateData> => {
      // Get the latest progress tracking for this collection
      const { data: progressData, error: progressError } = await supabase
        .from('progress_tracking')
        .select('*')
        .eq('collection_id', collectionId)
        .eq('process_type', 'product_upload')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (progressError && progressError.code !== 'PGRST116') {
        console.error('Error fetching progress:', progressError);
      }

      // Count total products in collection with original text
      const { count: totalCount, error: totalError } = await supabase
        .from('product_descriptions')
        .select('*', { count: 'exact', head: true })
        .eq('collection_id', collectionId)
        .not('original_text', 'is', null)
        .not('original_text', 'eq', '');

      if (totalError) {
        console.error('Error counting total products:', totalError);
        throw totalError;
      }

      // Count products with meaningful AI generated descriptions (not placeholders)
      // Placeholders are typically very short (< 50 characters) or contain placeholder text
      const { data: allProducts, error: allProductsError } = await supabase
        .from('product_descriptions')
        .select('generated_description, processing_status')
        .eq('collection_id', collectionId)
        .not('original_text', 'is', null)
        .not('original_text', 'eq', '');

      if (allProductsError) {
        console.error('Error fetching products for analysis:', allProductsError);
        throw allProductsError;
      }

      // Analyze descriptions to identify meaningful vs placeholder content
      const processed = allProducts?.filter(product => {
        const desc = product.generated_description;
        if (!desc || desc.trim() === '') return false;
        
        // Consider it a placeholder if:
        // 1. Very short (< 50 characters)
        // 2. Contains common placeholder patterns
        // 3. Processing status is not completed
        const isShort = desc.length < 50;
        const hasPlaceholderPattern = /^(Description for|Product description|Generated description|TBD|TODO|Placeholder|AI generated description)/i.test(desc);
        const isNotCompleted = product.processing_status !== 'completed';
        
        return !isShort && !hasPlaceholderPattern && !isNotCompleted;
      }).length || 0;

      // Count products that still need meaningful AI generation
      const pending = (totalCount || 0) - processed;

      const total = totalCount || 0;

      // Determine if we can resume:
      // 1. There are pending products to process
      // 2. Some products have already been processed (indicating a partial completion)
      // 3. The process is not currently running
      // 4. There's evidence of a stopped generation (progress tracking exists or error state)
      const hasStoppedGeneration = processed > 0 && pending > 0;
      const isCurrentlyProcessing = progressData?.status === 'processing';
      const hasErrorState = progressData?.status === 'error';
      const canResume = (hasStoppedGeneration || hasErrorState) && !isCurrentlyProcessing && pending > 0;

      return {
        canResume,
        pendingCount: pending,
        processedCount: processed,
        totalCount: total,
        hasStoppedGeneration
      };
    },
    refetchInterval: 5000, // Refetch every 5 seconds to keep state fresh
    staleTime: 1000, // Consider data stale after 1 second
  });
}